import { Component, OnDestroy, OnInit } from '@angular/core';
import { AppSettings } from '../../services/settings/app-settings.service';
import { Router } from '@angular/router';
import { CoreService } from '../../services/core/core.service';
import { OrganizationFilterService } from '../../services/organization-filter/organization-filter.service';
import { ServiceManagementData } from './components/service-management-card/service-management-card.component';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { ProductCatalogService } from '../../services/product-catalog/product-catalog.service';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  standalone: false,
})
export class HomeComponent implements OnInit, OnDestroy {
  // Service Management Data — se arranca vacío y se rellena cuando llegan el menú y las métricas.
  // Antes se inicializaba con TODO el catálogo, de modo que la vista pintaba las tarjetas de todos
  // los productos y luego las recortaba según los productos disponibles del usuario.
  public readonly serviceSkeletons = [1, 2, 3, 4];
  public services: ServiceManagementData[] = [];
  public hasVisibleServices = false;
  public isLoadingServices = true;
  public isLoadingMetrics = false;

  private orgFilterSub?: Subscription;
  private visibleServiceIds = new Set<string>();
  private deviceRoutes: Record<string, string> = {};
  private modelRoutes: Record<string, string> = {};

  constructor(
    private appSettings: AppSettings,
    private router: Router,
    public readonly coreService: CoreService,
    private readonly orgFilterService: OrganizationFilterService,
    private readonly productCatalog: ProductCatalogService
  ) {
    this.appSettings.appEmpty.set(false);
    this.appSettings.appHideGlobalHeader.set(false);
  }

  ngOnInit(): void {
    void this.initializeHome();
  }

  ngOnDestroy(): void {
    this.orgFilterSub?.unsubscribe();
  }

  private async initializeHome(): Promise<void> {
    try {
      await this.loadMenuConfiguration();
    } finally {
      this.isLoadingServices = false;
    }

    // Usar una única vía reactiva para evitar doble carga inicial.
    this.orgFilterSub = this.orgFilterService.selectedOrgIds$
      .pipe(
        distinctUntilChanged((prev, curr) => {
          if (prev.length !== curr.length) return false;
          return prev.every((value, index) => value === curr[index]);
        })
      )
      .subscribe((orgIds) => {
        this.loadServiceMetrics(orgIds);
      });
  }

  private async loadMenuConfiguration(): Promise<void> {
    try {
      const menuAccess = await this.coreService.getMenuAccess();
      const enabledServiceIds = this.coreService.getEnabledServiceIdsFromMenus(menuAccess);

      this.visibleServiceIds = new Set(enabledServiceIds);
      this.deviceRoutes = this.coreService.getServiceDeviceRoutesFromMenus(menuAccess);
      this.modelRoutes = this.coreService.getServiceModuleRoutesFromMenus(menuAccess, 'models');
      this.hasVisibleServices = this.visibleServiceIds.size > 0;
      this.services = this.filterVisibleServices(this.productCatalog.buildDefaultServiceManagementRows());
    } catch (error) {
      console.error('[HomeComponent] Error cargando configuración de menús:', error);
      this.visibleServiceIds = new Set();
      this.deviceRoutes = {};
      this.modelRoutes = {};
      this.hasVisibleServices = false;
      this.services = [];
    }
  }

  /**
   * Carga las métricas de servicios desde el API.
   * Si falla o no hay datos, los valores quedan en 0.
   */
  private async loadServiceMetrics(organizationIds?: string[]): Promise<void> {
    this.isLoadingMetrics = true;

    try {
      const filterIds = organizationIds && organizationIds.length > 0 ? organizationIds : undefined;

      const summaryData = await this.coreService.getServiceMetricsSummary(filterIds);
      this.services = this.filterVisibleServices(this.productCatalog.mergeServiceMetricsSummary(summaryData));
    } catch (error) {
      console.error('[HomeComponent] Error cargando métricas de servicios:', error);
      // En caso de error, mantener valores en 0
      this.services = this.filterVisibleServices(this.productCatalog.buildDefaultServiceManagementRows());
    } finally {
      this.isLoadingMetrics = false;
    }
  }

  private filterVisibleServices(services: ServiceManagementData[]): ServiceManagementData[] {
    if (this.visibleServiceIds.size === 0) {
      return [];
    }

    // Los menús guardados pueden nombrar al producto con su id anterior (`/home/products/qsense`):
    // se comparan ambos lados canonicalizados para que la tarjeta no desaparezca por ese motivo.
    const visibles = new Set(Array.from(this.visibleServiceIds).map((id) => this.productCatalog.canonicalServiceId(id)));
    return services.filter((service) => visibles.has(this.productCatalog.canonicalServiceId(service.id)));
  }

  /** Un acceso solo se ofrece si el menú del usuario publica esa vista para el producto. */
  canManageDevices(serviceId: string): boolean {
    return !!this.deviceRoutes[serviceId];
  }

  canManageModels(serviceId: string): boolean {
    return !!this.modelRoutes[serviceId];
  }

  onManageClients(serviceId: string) {
    console.log(`Navigating to manage clients for ${serviceId}`);
    // Navegar a la gestión de organizaciones con el filtro de servicio preseleccionado
    this.router.navigate(['/administration/organizations'], {
      queryParams: {
        service: serviceId,
      },
    });
  }

  onManageDevices(serviceId: string) {
    const route = this.deviceRoutes[serviceId];
    if (route) {
      this.router.navigate([route]);
    } else {
      console.warn(`[HomeComponent] No hay vista de dispositivos implementada para "${serviceId}"`);
    }
  }

  /**
   * La vista de modelos toma el producto del segmento `/home/products/<producto>/` de la url
   * (ModelsPageComponent), así que navegar a la ruta del menú ya deja el filtro de producto puesto.
   */
  onManageModels(serviceId: string) {
    const route = this.modelRoutes[serviceId];
    if (route) {
      this.router.navigate([route]);
    } else {
      console.warn(`[HomeComponent] No hay vista de modelos implementada para "${serviceId}"`);
    }
  }

  /**
   * Las tarjetas se reconstruyen al llegar las métricas del backend: sin `trackBy` se destruye y
   * recrea el DOM de todas, aunque solo cambien los contadores.
   */
  trackByServiceId(_index: number, service: { id: string }): string {
    return service.id;
  }
}
